import type {
  AdminBulkResponse,
  AdminLicenseDto,
  AdminTenantUserRole,
  AdminUserBulkAction,
  AdminUserCreateRequest,
  AdminUserDto,
  AdminUserPatchRequest,
  AdminUserRole,
  AdminUserStatus,
} from '../types';

export const ADMIN_USER_ROLES = ['Super', 'Admin', 'Manager', 'User'] as const;
export const ADMIN_TENANT_USER_ROLES = ['Admin', 'Manager', 'User'] as const;
export const ADMIN_USER_STATUSES = ['active', 'invited', 'blocked'] as const;

const UserRoles = new Set<AdminUserRole>(ADMIN_USER_ROLES);
const TenantUserRoles = new Set<AdminTenantUserRole>(ADMIN_TENANT_USER_ROLES);
const UserStatuses = new Set<AdminUserStatus>(ADMIN_USER_STATUSES);
const SortColumnIds = new Set([
  'name',
  'email',
  'company',
  'role',
  'licenses',
  'lastActive',
  'status',
]);
const SortQueryToColumn: Record<string, string> = {
  username: 'name',
  companyName: 'company',
  licenseIds: 'licenses',
  lastActiveAt: 'lastActive',
};
const SortColumnToQuery: Record<string, string> = {
  name: 'username',
  company: 'companyName',
  licenses: 'licenseIds',
  lastActive: 'lastActiveAt',
};

export interface UserTableFilters {
  [key: string]: string;
  name: string;
  email: string;
  company: string;
  role: string;
  status: string;
}

export interface UserTableQueryState {
  filters: UserTableFilters;
  sort: {
    columnId: string;
    direction: 'ascending' | 'descending';
  } | null;
}

export interface UserDraft {
  username: string;
  email: string;
  companyId: number;
  role: AdminUserRole;
  status: AdminUserStatus;
  licenseIds: number[];
}

export type UserDraftErrors = Partial<Record<keyof UserDraft, string>>;

export interface UserCompanyChange {
  draft: UserDraft;
  removedLicenseIds: number[];
}

const normalizeLicenseIds = (ids: readonly number[]): number[] =>
  [...new Set(ids)].sort((left, right) => left - right);

const sameLicenseIds = (left: readonly number[], right: readonly number[]): boolean => {
  const normalizedLeft = normalizeLicenseIds(left);
  const normalizedRight = normalizeLicenseIds(right);
  return normalizedLeft.length === normalizedRight.length
    && normalizedLeft.every((id, index) => id === normalizedRight[index]);
};

const isTenantRole = (role: AdminUserRole): role is AdminTenantUserRole =>
  TenantUserRoles.has(role as AdminTenantUserRole);

export const parseUserTableQuery = (query: URLSearchParams): UserTableQueryState => {
  const role = query.get('role') ?? '';
  const status = query.get('status') ?? '';
  const rawSort = query.get('sort') ?? '';
  const descending = rawSort.startsWith('-');
  const querySortId = descending ? rawSort.slice(1) : rawSort;
  const sortColumnId = SortQueryToColumn[querySortId] ?? querySortId;

  return {
    filters: {
      name: query.get('name') ?? '',
      email: query.get('email') ?? '',
      company: query.get('company') ?? '',
      role: UserRoles.has(role as AdminUserRole) ? role : '',
      status: UserStatuses.has(status as AdminUserStatus) ? status : '',
    },
    sort: SortColumnIds.has(sortColumnId)
      ? { columnId: sortColumnId, direction: descending ? 'descending' : 'ascending' }
      : null,
  };
};

export const serializeUserTableQuery = (
  state: UserTableQueryState
): URLSearchParams => {
  const query = new URLSearchParams();
  const entries = [
    ['name', state.filters.name],
    ['email', state.filters.email],
    ['company', state.filters.company],
    ['role', state.filters.role],
    ['status', state.filters.status],
  ] as const;
  entries.forEach(([key, value]) => {
    if (value !== '') {
      query.set(key, value);
    }
  });
  if (state.sort) {
    const prefix = state.sort.direction === 'descending' ? '-' : '';
    const queryColumn = SortColumnToQuery[state.sort.columnId] ?? state.sort.columnId;
    query.set('sort', `${prefix}${queryColumn}`);
  }
  return query;
};

export const formatAdminRelativeTime = (
  value: string | null,
  now: Date = new Date()
): string => {
  if (!value) {
    return 'Never';
  }
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) {
    return 'Never';
  }
  const elapsedSeconds = Math.max(0, Math.floor((now.getTime() - timestamp) / 1000));
  if (elapsedSeconds < 60) {
    return 'Just now';
  }
  const elapsedMinutes = Math.floor(elapsedSeconds / 60);
  if (elapsedMinutes < 60) {
    return `${elapsedMinutes}m ago`;
  }
  const elapsedHours = Math.floor(elapsedMinutes / 60);
  if (elapsedHours < 24) {
    return `${elapsedHours}h ago`;
  }
  return `${Math.floor(elapsedHours / 24)}d ago`;
};

export const filterCompanyLicenses = (
  licenses: readonly AdminLicenseDto[],
  companyId: number
): AdminLicenseDto[] => licenses.filter((license) => license.companyId === companyId);

export const createUserDraft = (user: AdminUserDto): UserDraft => ({
  username: user.username,
  email: user.email ?? '',
  companyId: user.companyId ?? 0,
  role: user.role,
  status: user.status,
  licenseIds: normalizeLicenseIds(user.licenseIds),
});

export const createNewUserDraft = (companyId = 0): UserDraft => ({
  username: '',
  email: '',
  companyId,
  role: 'User',
  status: 'invited',
  licenseIds: [],
});

export const changeUserDraftCompany = (
  draft: UserDraft,
  companyId: number,
  licenses: readonly AdminLicenseDto[]
): UserCompanyChange => {
  const ownedIds = new Set(filterCompanyLicenses(licenses, companyId).map((license) => license.id));
  const licenseIds = draft.licenseIds.filter((id) => ownedIds.has(id));
  const removedLicenseIds = draft.licenseIds.filter((id) => !ownedIds.has(id));
  return {
    draft: { ...draft, companyId, licenseIds },
    removedLicenseIds,
  };
};

export const hasDeliverableUserEmail = (value: string | null): boolean => {
  if (!value || value.length > 320 || /[\r\n]/.test(value)) {
    return false;
  }
  const email = value.trim();
  const parts = email.split('@');
  if (parts.length !== 2) {
    return false;
  }
  const [local, domain] = parts;
  const validLocal = local.length > 0
    && local.length <= 64
    && !local.startsWith('.')
    && !local.endsWith('.')
    && !local.includes('..')
    && /^[A-Z0-9.!#$%&'*+/=?^_`{|}~-]+$/i.test(local);
  const labels = domain.split('.');
  const validDomain = labels.length > 1 && labels.every((label) =>
    label.length > 0
    && label.length <= 63
    && !label.startsWith('-')
    && !label.endsWith('-')
    && /^[A-Z0-9-]+$/i.test(label));
  return validLocal && validDomain;
};

export const validateUserDraft = (
  draft: UserDraft,
  mode: 'invite' | 'edit',
  licenses: readonly AdminLicenseDto[],
  original?: UserDraft
): UserDraftErrors => {
  const errors: UserDraftErrors = {};
  if (mode === 'edit' && draft.username.trim() === '') {
    errors.username = 'Name is required.';
  } else if (draft.username.length > 255) {
    errors.username = 'Name must be 255 characters or fewer.';
  }
  if (mode === 'invite' && draft.email.trim() === '') {
    errors.email = 'Email is required for an invitation.';
  } else if (draft.email.trim() !== '' && !hasDeliverableUserEmail(draft.email)) {
    errors.email = 'Enter a valid email address.';
  }
  const keepsPlatformCompany = mode === 'edit'
    && draft.role === 'Super'
    && original?.role === 'Super'
    && original.companyId <= 0
    && draft.companyId === original.companyId;
  if (draft.companyId <= 0 && !keepsPlatformCompany) {
    errors.companyId = 'Choose a company.';
  }
  if (mode === 'invite' && !isTenantRole(draft.role)) {
    errors.role = 'Choose Admin, Manager, or User.';
  } else if (!UserRoles.has(draft.role)) {
    errors.role = 'Choose Super, Admin, Manager, or User.';
  }
  if (!UserStatuses.has(draft.status)) {
    errors.status = 'Choose active, invited, or blocked.';
  }
  const foreignIds = new Set(licenses
    .filter((license) => license.companyId !== draft.companyId)
    .map((license) => license.id));
  if (draft.licenseIds.some((id) => foreignIds.has(id))) {
    errors.licenseIds = 'Every assigned license must belong to the selected company.';
  }
  return errors;
};

export const isUserDraftDirty = (draft: UserDraft, original: UserDraft): boolean =>
  draft.username !== original.username
  || draft.email !== original.email
  || draft.companyId !== original.companyId
  || draft.role !== original.role
  || draft.status !== original.status
  || !sameLicenseIds(draft.licenseIds, original.licenseIds);

export const createUserCreateRequest = (draft: UserDraft): AdminUserCreateRequest => ({
  username: draft.username.trim(),
  email: draft.email.trim(),
  companyId: draft.companyId,
  role: draft.role as AdminTenantUserRole,
  licenseIds: normalizeLicenseIds(draft.licenseIds),
});

export const createUserPatchRequest = (
  draft: UserDraft,
  original: UserDraft
): AdminUserPatchRequest | null => {
  const patch: AdminUserPatchRequest = {};
  if (draft.username !== original.username) {
    patch.username = draft.username.trim();
  }
  if (draft.email !== original.email) {
    patch.email = draft.email.trim() || null;
  }
  if (draft.companyId !== original.companyId) {
    patch.companyId = draft.companyId;
  }
  if (draft.role !== original.role) {
    patch.role = draft.role;
  }
  if (draft.status !== original.status) {
    patch.status = draft.status;
  }
  if (!sameLicenseIds(draft.licenseIds, original.licenseIds)) {
    patch.licenseIds = normalizeLicenseIds(draft.licenseIds);
  }
  return Object.keys(patch).length > 0 ? patch : null;
};

export const isProtectedSuperUser = (
  user: AdminUserDto,
  users: readonly AdminUserDto[]
): boolean => user.role === 'Super'
  && user.status === 'active'
  && users.filter((candidate) => candidate.role === 'Super' && candidate.status === 'active')
    .length <= 1;

export const reduceUserSelectionAfterBulk = (
  selectedIds: readonly number[],
  result: AdminBulkResponse
): number[] => {
  const succeeded = new Set(result.succeeded);
  return selectedIds.filter((id) => !succeeded.has(id));
};

const BulkPastTense: Record<AdminUserBulkAction, string> = {
  block: 'Blocked',
  unblock: 'Unblocked',
  delete: 'Deleted',
};

const userCount = (count: number): string => `${count} ${count === 1 ? 'user' : 'users'}`;

export const formatUserBulkResult = (
  action: AdminUserBulkAction,
  result: AdminBulkResponse
): string => {
  const succeeded = `${BulkPastTense[action]} ${userCount(result.succeeded.length)}`;
  if (result.failed.length === 0) {
    return `${succeeded}.`;
  }
  const failures = result.failed
    .map((failure) => `#${failure.id} ${failure.code}`)
    .join(', ');
  return `${succeeded}; ${result.failed.length} failed: ${failures}.`;
};
